'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/components/ui/skeleton';
import { useVerificationStatement } from '@/features/ghg/api/use-verification-statements';
import { VerificationStatementPreview } from '@/features/ghg/components/VerificationStatementPreview';
import i18n from '@/lib/i18n';

export const Route = createFileRoute(
  '/_authenticated/ghg/verification-statements/$statementId'
)({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.verificationStatement'),
  }),
  component: VerificationStatementDetailPage,
});

function VerificationStatementDetailPage() {
  const { t } = useTranslation('ghg');
  const { statementId } = Route.useParams();
  const navigate = useNavigate();

  const { statement, isLoading, isError } = useVerificationStatement(statementId);

  const handleBack = () => {
    void navigate({ to: '/ghg/verification-statements' });
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-6xl space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-4 w-96" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !statement) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-6xl text-center">
          <h2 className="text-xl font-semibold text-foreground">{t('verificationStatementPreview.statementNotFound')}</h2>
          <p className="mt-2 text-muted-foreground">
            {t('verificationStatementPreview.statementNotLoaded')}
          </p>
          <button
            onClick={handleBack}
            className="mt-4 text-sm text-primary underline hover:no-underline"
          >
            {t('verificationStatementPreview.backToStatements')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <VerificationStatementPreview statement={statement} onBack={handleBack} />
      </div>
    </div>
  );
}
